import React, { useEffect, useState, useRef } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Paper, LinearProgress, Chip, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel, Slider, Popover,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { getProject, addLabel, deleteLabel, updateLabelColor, updateProject, listProjects, copyLabelToProject, rollupLabelsToProject } from '../../api/annotatorProjects';
import { previewImportToProject, importToProject } from '../../api/annotatorImport';
import type { ImportToProjectPreview } from '../../api/annotatorImport';
import type { AnnotatorProject, Label } from '../../types/annotator';
import { useAuth } from '../../contexts/AuthContext';
import { runProjectInference, runUploadedProjectInference } from '../../api/annotatorPipeline';
import type { InferResult, UploadedInferResult } from '../../api/annotatorPipeline';

interface CameraDevice {
  id: string;
  name: string;
}

interface InferenceModelOption {
  id: string;
  name: string;
  displayName: string;
  modelPath: string;
}

const DEFAULT_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#ec4899'];

export default function AnnotatorProjectPage() {
  const { projectId } = useParams();
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const [project, setProject] = useState<AnnotatorProject | null>(null);
  const [labelName, setLabelName] = useState('');
  const [labelColor, setLabelColor] = useState(DEFAULT_COLORS[0]);
  const [error, setError] = useState('');
  const [editingDetails, setEditingDetails] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [editingFolder, setEditingFolder] = useState(false);
  const [folderValue, setFolderValue] = useState('');
  const [folderSaving, setFolderSaving] = useState(false);

  // Pipeline state
  const [ftpDeviceId, setFtpDeviceId] = useState('');
  const [inferenceConfidence, setInferenceConfidence] = useState(0.5);
  const [inferenceModelId, setInferenceModelId] = useState<string>('');
  const [inferenceUseSourceSubdirectory, setInferenceUseSourceSubdirectory] = useState(false);
  const [inferenceSourceSubdirectory, setInferenceSourceSubdirectory] = useState('');
  const [cameraDevices, setCameraDevices] = useState<CameraDevice[]>([]);
  const [inferenceModelOptions, setInferenceModelOptions] = useState<InferenceModelOption[]>([]);
  const [pipelineSaving, setPipelineSaving] = useState(false);
  const [pipelineSuccess, setPipelineSuccess] = useState(false);
  const [lookbackMinutes, setLookbackMinutes] = useState(60);
  const [inferRunning, setInferRunning] = useState(false);
  const [inferResult, setInferResult] = useState<InferResult | null>(null);
  const [uploadInferRunning, setUploadInferRunning] = useState(false);
  const [uploadInferResult, setUploadInferResult] = useState<UploadedInferResult | null>(null);
  const [uploadFrameIntervalSeconds, setUploadFrameIntervalSeconds] = useState(1);
  const [uploadMaxFrames, setUploadMaxFrames] = useState(120);

  // Delete label state
  const [deleteDialogLabel, setDeleteDialogLabel] = useState<Label | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Copy label state
  const [copyDialogLabel, setCopyDialogLabel] = useState<Label | null>(null);
  const [allProjects, setAllProjects] = useState<AnnotatorProject[]>([]);
  const [copyDestProjectId, setCopyDestProjectId] = useState<number | ''>('');
  const [copyRemoveSrc, setCopyRemoveSrc] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyError, setCopyError] = useState('');
  const [copySuccess, setCopySuccess] = useState('');

  // Label colour picker state
  const [colorPickerAnchor, setColorPickerAnchor] = useState<{ el: HTMLElement; label: Label } | null>(null);
  const [colorSaving, setColorSaving] = useState(false);

  // Roll up labels state
  const [rollupSelected, setRollupSelected] = useState<Set<number>>(new Set());
  const [rollupDialogOpen, setRollupDialogOpen] = useState(false);
  const [rollupDestProjectId, setRollupDestProjectId] = useState<number | ''>('');
  const [rollupDestLabelName, setRollupDestLabelName] = useState('');
  const [rollupRemoveSrc, setRollupRemoveSrc] = useState(false);
  const [rollupLoading, setRollupLoading] = useState(false);
  const [rollupError, setRollupError] = useState('');
  const [rollupSuccess, setRollupSuccess] = useState('');

  // Import state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<ImportToProjectPreview | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const importFileRef = useRef<HTMLInputElement>(null);
  const uploadFolderRef = useRef<HTMLInputElement>(null);
  const uploadMediaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!projectId) return;
    getProject(Number(projectId)).then((p) => {
      setProject(p);
      setNameValue(p.name);
      setDescriptionValue(p.description ?? '');
      setFolderValue(p.output_folder ?? '');
      setFtpDeviceId(p.ftp_device_id ?? '');
      setInferenceConfidence(p.inference_confidence ?? 0.5);
      setInferenceModelId(p.inference_model_id ?? '');
      setInferenceUseSourceSubdirectory(p.inference_use_source_subdirectory ?? false);
      setInferenceSourceSubdirectory(p.inference_source_subdirectory ?? '');
    }).catch(console.error);
  }, [projectId]);

  useEffect(() => {
    fetch('/api/inference-models')
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data: any[]) => {
        setInferenceModelOptions(
          data
            .filter((m) => m.isActive)
            .map((m) => ({ id: m.id, name: m.name, displayName: m.displayName, modelPath: m.modelPath })),
        );
      })
      .catch(() => { /* non-fatal */ });
  }, []);

  useEffect(() => {
    if (!accessToken) return;
    fetch('/api/devices?skip=0&take=500', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data: { id: string; name: string; deviceType?: { deviceCategory?: string } }[]) => {
        const cameras = data
          .filter((d) => d.deviceType?.deviceCategory?.toLowerCase() === 'camera')
          .map((d) => ({ id: d.id, name: d.name }));
        setCameraDevices(cameras);
      })
      .catch(() => { /* non-fatal — free text still works */ });
  }, [accessToken]);

  async function handleSaveDetails() {
    if (!project) return;
    setDetailsSaving(true);
    try {
      const updated = await updateProject(project.id, { name: nameValue.trim(), description: descriptionValue.trim() });
      setProject(updated);
      setEditingDetails(false);
    } catch {
      setError('Failed to update project details.');
    } finally {
      setDetailsSaving(false);
    }
  }

  async function handleAddLabel(e: React.FormEvent) {
    e.preventDefault();
    if (!project) return;
    setError('');
    try {
      const label = await addLabel(project.id, { name: labelName, color: labelColor });
      setProject((prev) => prev ? { ...prev, labels: [...prev.labels, label] } : prev);
      setLabelName('');
    } catch {
      setError('Failed to add label.');
    }
  }

  function handleOpenDeleteDialog(label: Label) {
    setDeleteDialogLabel(label);
    setDeleteError('');
  }

  async function handleConfirmDeleteLabel() {
    if (!project || !deleteDialogLabel) return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await deleteLabel(project.id, deleteDialogLabel.id);
      setProject((prev) => prev ? { ...prev, labels: prev.labels.filter((l) => l.id !== deleteDialogLabel.id) } : prev);
      // Image counts may have dropped if some were solely annotated with this label
      const updated = await getProject(project.id);
      setProject(updated);
      setDeleteDialogLabel(null);
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete label.');
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleOpenCopyDialog(label: Label) {
    setCopyDialogLabel(label);
    setCopyDestProjectId('');
    setCopyRemoveSrc(false);
    setCopyError('');
    setCopySuccess('');
    if (allProjects.length === 0) {
      listProjects().then(setAllProjects).catch(() => {});
    }
  }

  async function handleCopyLabel() {
    if (!project || !copyDialogLabel || copyDestProjectId === '') return;
    setCopyLoading(true);
    setCopyError('');
    setCopySuccess('');
    try {
      const result = await copyLabelToProject(
        project.id,
        copyDialogLabel.id,
        copyDestProjectId as number,
        copyRemoveSrc,
      );
      setCopySuccess(`Copied ${result.copied_images} image${result.copied_images !== 1 ? 's' : ''} to destination project.`);
      if (copyRemoveSrc) {
        // Refresh project so the removed label disappears
        const updated = await getProject(project.id);
        setProject(updated);
      }
    } catch (err: any) {
      setCopyError(err.message || 'Copy failed');
    } finally {
      setCopyLoading(false);
    }
  }

  function openColorPicker(e: React.MouseEvent<HTMLElement>, label: Label) {
    setColorPickerAnchor({ el: e.currentTarget, label });
  }

  async function handlePickColor(color: string) {
    if (!project || !colorPickerAnchor) return;
    setColorSaving(true);
    try {
      const updated = await updateLabelColor(project.id, colorPickerAnchor.label.id, color);
      setProject({ ...project, labels: project.labels.map((l) => (l.id === updated.id ? updated : l)) });
    } finally {
      setColorSaving(false);
      setColorPickerAnchor(null);
    }
  }

  function toggleRollupLabel(labelId: number) {
    setRollupSelected((prev) => {
      const next = new Set(prev);
      if (next.has(labelId)) next.delete(labelId); else next.add(labelId);
      return next;
    });
  }

  function openRollupDialog() {
    setRollupDestProjectId('');
    setRollupDestLabelName('');
    setRollupRemoveSrc(false);
    setRollupError('');
    setRollupSuccess('');
    setRollupDialogOpen(true);
    if (allProjects.length === 0) {
      listProjects().then(setAllProjects).catch(() => {});
    }
  }

  async function handleRollup() {
    if (!project || rollupDestProjectId === '' || !rollupDestLabelName.trim() || rollupSelected.size === 0) return;
    setRollupLoading(true);
    setRollupError('');
    try {
      const name = rollupDestLabelName.trim();
      const result = await rollupLabelsToProject(
        project.id,
        Array.from(rollupSelected),
        rollupDestProjectId as number,
        name,
        rollupRemoveSrc,
      );
      setRollupSuccess(`Merged ${result.copied_images} image${result.copied_images !== 1 ? 's' : ''} into "${name}".`);
      setRollupSelected(new Set());
      if (rollupRemoveSrc) {
        const updated = await getProject(project.id);
        setProject(updated);
      }
    } catch (err: any) {
      setRollupError(err.message || 'Roll-up failed');
    } finally {
      setRollupLoading(false);
    }
  }

  async function handleSaveFolder() {
    if (!project) return;
    setFolderSaving(true);
    try {
      const updated = await updateProject(project.id, { output_folder: folderValue.trim() });
      setProject(updated);
      setEditingFolder(false);
    } catch {
      setError('Failed to update output folder.');
    } finally {
      setFolderSaving(false);
    }
  }

  function handleInferenceModelChange(modelId: string) {
    setInferenceModelId(modelId);
    if (modelId) {
      const m = inferenceModelOptions.find((o) => o.id === modelId);
      if (m) {
        setFolderValue(`/app/training/trainingdata/${m.name}`);
      }
    }
  }

  async function handleSavePipeline() {
    if (!project) return;
    setPipelineSaving(true);
    setPipelineSuccess(false);
    try {
      const updated = await updateProject(project.id, {
        ftp_device_id: ftpDeviceId.trim() || '',
        inference_confidence: inferenceConfidence,
        inference_model_id: inferenceModelId || null,
        inference_use_source_subdirectory: inferenceUseSourceSubdirectory,
        inference_source_subdirectory: inferenceSourceSubdirectory.trim(),
        output_folder: folderValue.trim() || '',
      });
      setProject(updated);
      setInferenceModelId(updated.inference_model_id ?? '');
      setFolderValue(updated.output_folder ?? '');
      setInferenceUseSourceSubdirectory(updated.inference_use_source_subdirectory ?? false);
      setInferenceSourceSubdirectory(updated.inference_source_subdirectory ?? '');
      setPipelineSuccess(true);
      setTimeout(() => setPipelineSuccess(false), 3000);
    } catch {
      setError('Failed to update pipeline settings.');
    } finally {
      setPipelineSaving(false);
    }
  }

  async function handleRunInference() {
    if (!project) return;
    setInferRunning(true);
    setInferResult(null);
    try {
      // Save pipeline config first so the UUID / confidence is current
      await updateProject(project.id, {
        ftp_device_id: ftpDeviceId.trim() || '',
        inference_confidence: inferenceConfidence,
        inference_model_id: inferenceModelId || null,
        inference_use_source_subdirectory: inferenceUseSourceSubdirectory,
        inference_source_subdirectory: inferenceSourceSubdirectory.trim(),
        output_folder: folderValue.trim() || '',
      });
      const result = await runProjectInference(project.id, lookbackMinutes);
      setInferResult(result);
      // Refresh project to update unverified count
      const updated = await getProject(project.id);
      setProject(updated);
    } catch (err: any) {
      setInferResult({ imported: 0, errors: 1, files_checked: 0, error: err.message || 'Inference failed' });
    } finally {
      setInferRunning(false);
    }
  }

  async function handleUploadedInference(files: File[]) {
    if (!project || files.length === 0) return;
    setUploadInferRunning(true);
    setUploadInferResult(null);
    try {
      await updateProject(project.id, {
        ftp_device_id: ftpDeviceId.trim() || '',
        inference_confidence: inferenceConfidence,
        inference_model_id: inferenceModelId || null,
        inference_use_source_subdirectory: inferenceUseSourceSubdirectory,
        inference_source_subdirectory: inferenceSourceSubdirectory.trim(),
        output_folder: folderValue.trim() || '',
      });
      const result = await runUploadedProjectInference(project.id, files, {
        frameIntervalSeconds: uploadFrameIntervalSeconds,
        maxFrames: uploadMaxFrames,
      });
      setUploadInferResult(result);
      const updated = await getProject(project.id);
      setProject(updated);
    } catch (err: any) {
      setUploadInferResult({
        files_received: files.length,
        files_checked: 0,
        imported: 0,
        errors: 1,
        skipped_no_detection: 0,
        skipped_unsupported: 0,
        frames_extracted: 0,
        error: err.message || 'Uploaded inference failed',
      });
    } finally {
      setUploadInferRunning(false);
    }
  }

  function handleFolderInferenceSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    void handleUploadedInference(files);
    e.target.value = '';
  }

  function handleMediaInferenceSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    void handleUploadedInference(files);
    e.target.value = '';
  }

  function handleImportFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !project) return;
    setImportFile(file);
    setImportError('');
    setImportPreview(null);
    setImportLoading(true);
    setImportOpen(true);
    previewImportToProject(project.id, file)
      .then(setImportPreview)
      .catch((err) => setImportError(err.message || 'Failed to parse ZIP'))
      .finally(() => setImportLoading(false));
    // Reset input so same file can be re-selected
    e.target.value = '';
  }

  async function handleImportExecute() {
    if (!project || !importFile) return;
    setImportLoading(true);
    setImportError('');
    try {
      const updated = await importToProject(project.id, importFile, true);
      setProject(updated);
      setImportOpen(false);
      setImportFile(null);
      setImportPreview(null);
    } catch (err: any) {
      setImportError(err.message || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  }

  function handleImportClose() {
    if (!importLoading) {
      setImportOpen(false);
      setImportFile(null);
      setImportPreview(null);
      setImportError('');
    }
  }

  if (!project) return <Typography color="text.secondary">Loading...</Typography>;

  const pct = project.image_count > 0 ? Math.round((project.annotated_count / project.image_count) * 100) : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          {editingDetails ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField size="small" value={nameValue} onChange={(e) => setNameValue(e.target.value)} label="Name" />
              <TextField size="small" multiline rows={2} value={descriptionValue} onChange={(e) => setDescriptionValue(e.target.value)} label="Description" />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={handleSaveDetails} disabled={detailsSaving}>Save</Button>
                <Button size="small" onClick={() => setEditingDetails(false)}>Cancel</Button>
              </Box>
            </Box>
          ) : (
            <>
              <Typography variant="h5" fontWeight={600}>{project.name}</Typography>
              {project.description && <Typography color="text.secondary">{project.description}</Typography>}
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<EditIcon />} onClick={() => setEditingDetails((v) => !v)}>Edit</Button>
          <Button variant="contained" component={RouterLink} to={`/annotator/projects/${project.id}/images`}>
            Images ({project.image_count})
          </Button>
          <input ref={importFileRef} type="file" accept=".zip" hidden onChange={handleImportFileSelect} />
          <input
            ref={uploadFolderRef}
            type="file"
            hidden
            multiple
            onChange={handleFolderInferenceSelect}
            {...({ webkitdirectory: 'true', directory: 'true' } as any)}
          />
          <input
            ref={uploadMediaRef}
            type="file"
            hidden
            multiple
            accept="image/*,.mp4,video/mp4"
            onChange={handleMediaInferenceSelect}
          />
          <Button startIcon={<UploadFileIcon />} onClick={() => importFileRef.current?.click()}>Import</Button>
          <Button component={RouterLink} to={`/annotator/projects/${project.id}/export`}>Export</Button>
          <Button component={RouterLink} to={`/annotator/projects/${project.id}/training`}>Train</Button>
          <Button component={RouterLink} to={`/annotator/projects/${project.id}/detection-test`}>Test</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">{project.annotated_count} / {project.image_count} annotated</Typography>
          <Typography variant="body2" color="text.secondary">{pct}%</Typography>
        </Box>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Output Folder</Typography>
        {editingFolder ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField size="small" fullWidth value={folderValue} onChange={(e) => setFolderValue(e.target.value)} sx={{ fontFamily: 'monospace' }} />
            <Button size="small" variant="contained" onClick={handleSaveFolder} disabled={folderSaving}>Save</Button>
            <Button size="small" onClick={() => setEditingFolder(false)}>Cancel</Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', flex: 1 }}>
              {project.output_folder || <em>not set</em>}
            </Typography>
            <Button size="small" onClick={() => setEditingFolder(true)}>Edit</Button>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Auto-Inference Pipeline</Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              freeSolo
              options={cameraDevices}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.name} (${opt.id})`}
              value={cameraDevices.find((d) => d.id === ftpDeviceId) ?? ftpDeviceId}
              onChange={(_, newValue) => {
                if (!newValue) { setFtpDeviceId(''); }
                else if (typeof newValue === 'string') { setFtpDeviceId(newValue); }
                else { setFtpDeviceId(newValue.id); }
              }}
              onInputChange={(_, inputValue, reason) => {
                if (reason === 'input') setFtpDeviceId(inputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="FTP Device UUID"
                  placeholder="Select a camera device or paste a UUID"
                  helperText={ftpDeviceId ? `UUID: ${ftpDeviceId}` : 'Select a camera device. Leave blank to disable scanning.'}
                />
              )}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={inferenceUseSourceSubdirectory}
                  onChange={(e) => setInferenceUseSourceSubdirectory(e.target.checked)}
                />
              }
              label="Only scan a subdirectory under this device folder"
            />
            <TextField
              size="small"
              label="Source subdirectory"
              placeholder="pictures/Snapshots"
              value={inferenceSourceSubdirectory}
              onChange={(e) => setInferenceSourceSubdirectory(e.target.value)}
              disabled={!inferenceUseSourceSubdirectory}
              helperText={
                inferenceUseSourceSubdirectory
                  ? 'Relative path under the selected device folder. Example: pictures/Snapshots'
                  : 'Leave disabled to scan the whole device folder.'
              }
            />
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Inference Confidence Threshold: <strong>{inferenceConfidence.toFixed(2)}</strong>
              </Typography>
              <Slider
                value={inferenceConfidence}
                onChange={(_, v) => setInferenceConfidence(v as number)}
                min={0.1}
                max={0.9}
                step={0.05}
                marks={[{ value: 0.1, label: '0.1' }, { value: 0.5, label: '0.5' }, { value: 0.9, label: '0.9' }]}
                size="small"
              />
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel id="inference-model-label">Inference Model</InputLabel>
              <Select
                labelId="inference-model-label"
                label="Inference Model"
                value={inferenceModelId}
                onChange={(e) => handleInferenceModelChange(e.target.value)}
              >
                <MenuItem value=""><em>None (use project/folder name fallback)</em></MenuItem>
                {inferenceModelOptions.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.displayName}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSavePipeline}
                disabled={pipelineSaving}
              >
                {pipelineSaving ? 'Saving…' : 'Save Settings'}
              </Button>
              {pipelineSuccess && <Typography variant="body2" color="success.main">✓ Saved</Typography>}
            </Box>

            {project.unverified_count > 0 && (
              <Alert severity="warning">
                {project.unverified_count} unverified image{project.unverified_count !== 1 ? 's' : ''} pending review.{' '}
                <RouterLink to={`/annotator/projects/${project.id}/images?filter=unverified`}>Review →</RouterLink>
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.default',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Start Auto Inference — scans the configured source for recent images, runs YOLO, and queues detections as unverified.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  type="number"
                  label="Minutes ago"
                  value={lookbackMinutes}
                  onChange={(e) => setLookbackMinutes(Math.max(1, Number(e.target.value)))}
                  inputProps={{ min: 1 }}
                  sx={{ width: 130 }}
                  helperText="Look back this far"
                />
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleRunInference}
                  disabled={inferRunning || !ftpDeviceId}
                  sx={{ mt: 0.25 }}
                >
                  {inferRunning ? <><CircularProgress size={14} sx={{ mr: 1 }} />Running…</> : 'Start Auto Inference'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSavePipeline}
                  disabled={pipelineSaving}
                  sx={{ mt: 0.25 }}
                >
                  {pipelineSaving ? 'Saving…' : 'Save Settings'}
                </Button>
                {pipelineSuccess && <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>✓ Saved</Typography>}
              </Box>
              {inferResult && (
                <Alert
                  severity={inferResult.error ? 'error' : inferResult.imported > 0 ? 'success' : 'info'}
                  sx={{ mt: 1.5 }}
                >
                  {inferResult.error
                    ? inferResult.error
                    : `Checked ${inferResult.files_checked} file(s) — ${inferResult.imported} imported, ${inferResult.errors} error(s).`}
                </Alert>
              )}
            </Box>

            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.default',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Upload Media for Inference — choose a local folder of images or upload images / MP4 files. Videos are sampled into frames before inference.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mb: 1 }}>
                <TextField
                  size="small"
                  type="number"
                  label="Frame interval (sec)"
                  value={uploadFrameIntervalSeconds}
                  onChange={(e) => setUploadFrameIntervalSeconds(Math.max(0.1, Number(e.target.value) || 0.1))}
                  inputProps={{ min: 0.1, step: 0.1 }}
                  sx={{ width: 150 }}
                  helperText="MP4 sampling interval"
                />
                <TextField
                  size="small"
                  type="number"
                  label="Max frames / video"
                  value={uploadMaxFrames}
                  onChange={(e) => setUploadMaxFrames(Math.max(1, Number(e.target.value) || 1))}
                  inputProps={{ min: 1, max: 5000 }}
                  sx={{ width: 150 }}
                  helperText="Stops long videos exploding"
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => uploadFolderRef.current?.click()}
                  disabled={uploadInferRunning || !ftpDeviceId}
                  sx={{ mt: 0.25 }}
                >
                  Select Folder
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => uploadMediaRef.current?.click()}
                  disabled={uploadInferRunning || !ftpDeviceId}
                  sx={{ mt: 0.25 }}
                >
                  Upload Images / MP4
                </Button>
                {uploadInferRunning && <Typography variant="body2" sx={{ mt: 0.5 }}>Uploading and inferencing…</Typography>}
              </Box>
              {uploadInferResult && (
                <Alert
                  severity={uploadInferResult.error ? 'error' : uploadInferResult.imported > 0 ? 'success' : 'info'}
                  sx={{ mt: 1.5 }}
                >
                  {uploadInferResult.error
                    ? uploadInferResult.error
                    : `Received ${uploadInferResult.files_received} file(s), checked ${uploadInferResult.files_checked} image/frame(s), imported ${uploadInferResult.imported}, extracted ${uploadInferResult.frames_extracted} frame(s), skipped ${uploadInferResult.skipped_no_detection} with no detections and ${uploadInferResult.skipped_unsupported} unsupported file(s), ${uploadInferResult.errors} error(s).`}
                </Alert>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2">Labels</Typography>
          {rollupSelected.size > 0 && (
            <Button size="small" startIcon={<MergeTypeIcon />} onClick={openRollupDialog}>
              Roll up {rollupSelected.size} label{rollupSelected.size !== 1 ? 's' : ''} to project…
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {project.labels.map((l) => (
            <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Checkbox
                size="small"
                checked={rollupSelected.has(l.id)}
                onChange={() => toggleRollupLabel(l.id)}
                title="Select for roll-up"
                sx={{ p: 0.25 }}
              />
              <Chip
                label={l.name}
                onDelete={() => handleOpenDeleteDialog(l)}
                onClick={(e) => openColorPicker(e, l)}
                title="Click to change colour"
                sx={{ borderLeft: `4px solid ${l.color}`, cursor: 'pointer' }}
              />
              <IconButton size="small" title="Copy to another project" onClick={() => handleOpenCopyDialog(l)}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Box>
          ))}
        </Box>
        <form onSubmit={handleAddLabel}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField size="small" value={labelName} onChange={(e) => setLabelName(e.target.value)} placeholder="Label name" required />
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {DEFAULT_COLORS.map((c) => (
                <IconButton
                  key={c}
                  size="small"
                  onClick={() => setLabelColor(c)}
                  sx={{
                    width: 24, height: 24, backgroundColor: c, borderRadius: '50%',
                    border: labelColor === c ? '2px solid white' : '2px solid transparent',
                    '&:hover': { backgroundColor: c },
                  }}
                />
              ))}
              <input type="color" value={labelColor} onChange={(e) => setLabelColor(e.target.value)} style={{ width: 28, height: 28, border: 'none', cursor: 'pointer' }} />
            </Box>
            <Button type="submit" variant="contained" size="small">Add Label</Button>
          </Box>
        </form>
      </Paper>

      {/* Label Colour Picker */}
      <Popover
        open={!!colorPickerAnchor}
        anchorEl={colorPickerAnchor?.el}
        onClose={() => setColorPickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {DEFAULT_COLORS.map((c) => (
            <IconButton
              key={c}
              size="small"
              onClick={() => handlePickColor(c)}
              disabled={colorSaving}
              sx={{
                width: 24, height: 24, backgroundColor: c, borderRadius: '50%',
                border: colorPickerAnchor?.label.color === c ? '2px solid white' : '2px solid transparent',
                '&:hover': { backgroundColor: c },
              }}
            />
          ))}
          <input
            type="color"
            value={colorPickerAnchor?.label.color ?? '#FF0000'}
            onChange={(e) => handlePickColor(e.target.value)}
            style={{ width: 28, height: 28, border: 'none', cursor: 'pointer' }}
            disabled={colorSaving}
          />
        </Box>
      </Popover>

      {/* Delete Label Confirmation Dialog */}
      <Dialog open={!!deleteDialogLabel} onClose={() => !deleteLoading && setDeleteDialogLabel(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Label "{deleteDialogLabel?.name}"?</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            <Alert severity="warning">
              This permanently deletes the label and its{' '}
              {deleteDialogLabel?.annotation_count ?? 0} annotation{deleteDialogLabel?.annotation_count === 1 ? '' : 's'} from this
              project. Any images left with no annotations afterwards are removed too. This cannot be undone.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              To bring this class back later with a fresh or improved dataset, use Import — a class name that matches
              will be re-added seamlessly.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogLabel(null)} disabled={deleteLoading}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteLabel}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Label Dialog */}
      <Dialog open={!!copyDialogLabel} onClose={() => !copyLoading && setCopyDialogLabel(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy Label "{copyDialogLabel?.name}" to Another Project</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {copyError && <Alert severity="error">{copyError}</Alert>}
            {copySuccess && <Alert severity="success">{copySuccess}</Alert>}
            <FormControl fullWidth size="small">
              <InputLabel>Destination Project</InputLabel>
              <Select
                value={copyDestProjectId}
                label="Destination Project"
                onChange={(e) => setCopyDestProjectId(e.target.value as number)}
                disabled={copyLoading || !!copySuccess}
              >
                {allProjects
                  .filter((p) => p.id !== project?.id)
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={copyRemoveSrc}
                  onChange={(e) => setCopyRemoveSrc(e.target.checked)}
                  disabled={copyLoading || !!copySuccess}
                />
              }
              label="Remove from this project after copying"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogLabel(null)} disabled={copyLoading}>Close</Button>
          {!copySuccess && (
            <Button
              variant="contained"
              onClick={handleCopyLabel}
              disabled={copyLoading || copyDestProjectId === ''}
              startIcon={copyLoading ? <CircularProgress size={16} /> : <ContentCopyIcon />}
            >
              {copyLoading ? 'Copying…' : 'Copy'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Roll Up Labels Dialog */}
      <Dialog open={rollupDialogOpen} onClose={() => !rollupLoading && setRollupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Roll Up {rollupSelected.size} Label{rollupSelected.size !== 1 ? 's' : ''} into a New Class</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Merges {project?.labels.filter((l) => rollupSelected.has(l.id)).map((l) => l.name).join(', ')} into
              one label in the destination project. An image with boxes for more than one of these ends up with
              multiple boxes under the merged label there, not duplicated images.
            </Typography>
            {rollupError && <Alert severity="error">{rollupError}</Alert>}
            {rollupSuccess && <Alert severity="success">{rollupSuccess}</Alert>}
            <FormControl fullWidth size="small">
              <InputLabel>Destination Project</InputLabel>
              <Select
                value={rollupDestProjectId}
                label="Destination Project"
                onChange={(e) => setRollupDestProjectId(e.target.value as number)}
                disabled={rollupLoading || !!rollupSuccess}
              >
                {allProjects
                  .filter((p) => p.id !== project?.id)
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              label="New Class Name"
              value={rollupDestLabelName}
              onChange={(e) => setRollupDestLabelName(e.target.value)}
              placeholder="e.g. bird"
              size="small"
              fullWidth
              disabled={rollupLoading || !!rollupSuccess}
              helperText="Created in the destination project if it doesn't already exist there"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rollupRemoveSrc}
                  onChange={(e) => setRollupRemoveSrc(e.target.checked)}
                  disabled={rollupLoading || !!rollupSuccess}
                />
              }
              label="Remove these labels from this project after rolling up"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollupDialogOpen(false)} disabled={rollupLoading}>Close</Button>
          {!rollupSuccess && (
            <Button
              variant="contained"
              onClick={handleRollup}
              disabled={rollupLoading || rollupDestProjectId === '' || !rollupDestLabelName.trim()}
              startIcon={rollupLoading ? <CircularProgress size={16} /> : <MergeTypeIcon />}
            >
              {rollupLoading ? 'Rolling up…' : 'Roll Up'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Import YOLO ZIP Dialog */}
      <Dialog open={importOpen} onClose={handleImportClose} maxWidth="sm" fullWidth>
        <DialogTitle>Import YOLO Dataset</DialogTitle>
        <DialogContent>
          {importLoading && !importPreview && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
          {importPreview && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{importPreview.image_count}</strong> images, <strong>{importPreview.label_file_count}</strong> label files
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Class mapping:</Typography>
              {importPreview.class_mapping.map((m) => (
                <Box key={m.zip_index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, pl: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{m.zip_class}</Typography>
                  <Typography variant="body2" color="text.secondary">→</Typography>
                  {m.matched_label_name ? (
                    <Chip label={m.matched_label_name} size="small" color="success" variant="outlined" />
                  ) : (
                    <Chip label="new label" size="small" color="warning" variant="outlined" />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportClose} disabled={importLoading}>Cancel</Button>
          <Button
            onClick={handleImportExecute}
            variant="contained"
            disabled={importLoading || !importPreview}
            startIcon={importLoading ? <CircularProgress size={16} /> : undefined}
          >
            {importLoading ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
